import { useEffect, useRef, useState } from "react";
import { api, media } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Upload, Trash2, Link2 } from "lucide-react";

const useFiles = (active) => {
  const [files, setFiles] = useState([]);
  const load = () => api.get("/admin/files").then(({ data }) => setFiles(data)).catch(() => {});
  useEffect(() => { if (active) load(); }, [active]);
  return [files, setFiles, load];
};

const Body = ({ onSelect }) => {
  const [files, setFiles, load] = useFiles(true);
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState("");
  const inputRef = useRef();

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      setFiles((f) => [data, ...f]);
    } catch { toast.error("Upload failed"); }
    setBusy(false);
    e.target.value = "";
  };

  const del = async (id) => {
    try { await api.delete(`/admin/files/${id}`); setFiles((f) => f.filter((x) => x.id !== id)); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <input ref={inputRef} type="file" accept="image/*,application/pdf" hidden onChange={upload} data-testid="media-upload-input" />
        <Button onClick={() => inputRef.current?.click()} disabled={busy} className="btn-primary rounded-lg shrink-0" data-testid="media-upload-btn">
          <Upload size={14} className="mr-2" />{busy ? "Uploading…" : "Upload"}
        </Button>
        <div className="flex-1 flex gap-2">
          <Input placeholder="or paste an image URL" value={url} onChange={(e) => setUrl(e.target.value)} className="bg-black/40 border-white/10" data-testid="media-url-input" />
          <Button variant="outline" onClick={() => { if (url) { onSelect(url); setUrl(""); } }} data-testid="media-url-use"><Link2 size={14} /></Button>
        </div>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 max-h-[52vh] overflow-y-auto mt-4 pr-1">
        {files.map((f) => (
          <div key={f.id} className="relative group rounded-xl overflow-hidden border border-white/10 aspect-square bg-black/40" data-testid={`media-item-${f.id}`}>
            <img src={media(f.url)} alt={f.original_filename} className="w-full h-full object-cover cursor-pointer" onClick={() => onSelect(f.url)} />
            <button onClick={() => del(f.id)} className="absolute top-1 right-1 p-1 rounded bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`media-del-${f.id}`}>
              <Trash2 size={12} className="text-rose-400" />
            </button>
          </div>
        ))}
        {files.length === 0 && <div className="col-span-full text-center text-slate-500 text-sm py-12">No files yet. Upload one to get started.</div>}
      </div>
    </div>
  );
};

export const MediaManager = ({ open, onOpenChange, onSelect }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="glass-strong border-white/10 max-w-3xl" data-testid="media-manager">
      <DialogHeader><DialogTitle className="font-serif text-2xl">Media Library</DialogTitle>
        <DialogDescription className="sr-only">Upload a new image or select an existing one.</DialogDescription>
      </DialogHeader>
      <Body onSelect={(u) => { onSelect(u); onOpenChange(false); }} />
    </DialogContent>
  </Dialog>
);

export const MediaLibrary = () => (
  <div>
    <h1 className="font-serif text-4xl text-white mb-6">Media Library</h1>
    <div className="glass rounded-2xl p-5">
      <Body onSelect={(u) => { navigator.clipboard?.writeText(media(u)); toast.success("URL copied to clipboard"); }} />
    </div>
  </div>
);
